"use client";
import {
  BettingTrophy,
  ColouredIconsFootball,
} from "@fanduel/formation-react-icons";
import cx from "classnames";
import Image from "next/image";
import { useEffect, useState } from "react";
import {
  HEAD_TO_HEAD_STATUS_MAP,
  HEAD_TO_HEAD_TEAM_LINK_CLICKED,
  SPORTSBOOK_URL,
} from "@/constants/amplitude";
import { SCREENS } from "@/constants/breakpoints";
import { TIME_IN_MS } from "@/constants/time";
import { BasicTeam, Event, EventStatus } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import useGameScores from "@/utils/hooks/useGameScores";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import useMedia from "@/utils/hooks/useMedia";
import { formatNFLRecord } from "../headerUtil";
import { MatchupBasicTeam } from "./MatchupMainWrapper";
import styles from "./livePostMatchupMain.module.scss";

interface LivePostMatchupMainProps {
  homeTeam: MatchupBasicTeam;
  awayTeam: MatchupBasicTeam;
}

const LivePostMatchupMain = ({
  homeTeam,
  awayTeam,
}: LivePostMatchupMainProps) => {
  const {
    gameId,
    gameState,
    scoreboard: initialScoreboard,
  } = useHeadToHeadContext();
  const isLive = gameState === EventStatus.Live;
  const isFinal = gameState === EventStatus.Final;
  const [isClient, setIsClient] = useState(false);

  const isTabletUp = useMedia(`(min-width:${SCREENS.MD})`, true);

  useEffect(() => {
    setIsClient(true);
  }, []);
  const reactQueryOptions = {
    initialData: { event: { scoreboard: initialScoreboard } },
    enabled: isLive,
    refetchInterval: isLive ? TIME_IN_MS.SEC_30 : undefined,
  };
  const { data } = useGameScores(gameId, reactQueryOptions);
  const scoreboard = data?.event?.scoreboard;
  const homeWin =
    isFinal &&
    scoreboard?.finalScore?.home != null &&
    scoreboard.finalScore.away != null &&
    scoreboard.finalScore.home > scoreboard.finalScore.away;
  const awayWin =
    isFinal &&
    scoreboard?.finalScore?.home != null &&
    scoreboard.finalScore.away != null &&
    scoreboard.finalScore.away > scoreboard.finalScore.home;

  const homeName =
    isTabletUp && isClient ? homeTeam.name : homeTeam.abbreviation;
  const awayName =
    isTabletUp && isClient ? awayTeam.name : awayTeam.abbreviation;
  const homeTimeouts = scoreboard?.timeoutsRemaining?.home;
  const awayTimeouts = scoreboard?.timeoutsRemaining?.away;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.awayTeam}>
          <div className={styles.logoSpacer} />
          <TeamText
            teamName={awayName ?? "Away"}
            sbkUrl={awayTeam.teamSportsbookUrl}
            isWinner={awayWin}
            opponentWin={homeWin}
            teamScore={scoreboard?.finalScore?.away}
            isTabletUp={isTabletUp}
            teamRecord={awayTeam.record}
            isHome={false}
            hasPossession={isLive && scoreboard?.possession === "away"}
            timeouts={awayTimeouts}
            isClient={isClient}
          />
          {isTabletUp && isClient && (
            <div className={cx(styles.score, { [styles.loss]: homeWin })}>
              {scoreboard?.finalScore?.away ?? "-"}
              {isLive && scoreboard?.possession === "away" && (
                <ColouredIconsFootball className={styles.awayTrophy} />
              )}
              {isFinal && awayWin && (
                <BettingTrophy className={styles.awayTrophy} />
              )}
            </div>
          )}
        </div>
        {isTabletUp && isClient && (
          <ScoreTable
            scoreboard={scoreboard}
            homeTeam={homeTeam}
            awayTeam={awayTeam}
          />
        )}
        <span
          className={cx(styles.gameStatus, isLive && styles.gameStatusLive)}
        >
          {isFinal ? "FINAL" : (scoreboard?.clock ?? "")}
        </span>

        <div className={styles.homeTeam}>
          {isTabletUp && isClient && (
            <div className={cx(styles.score, { [styles.loss]: awayWin })}>
              {scoreboard?.finalScore?.home ?? "-"}
              {isFinal && homeWin && (
                <BettingTrophy className={styles.homeTrophy} />
              )}
              {isLive && scoreboard?.possession === "home" && (
                <ColouredIconsFootball className={styles.homeTrophy} />
              )}
            </div>
          )}
          <TeamText
            teamName={homeName ?? "Home"}
            sbkUrl={homeTeam.teamSportsbookUrl}
            isWinner={homeWin}
            opponentWin={awayWin}
            teamScore={scoreboard?.finalScore?.home}
            teamRecord={homeTeam.record}
            isTabletUp={isTabletUp}
            isHome
            hasPossession={isLive && scoreboard?.possession === "home"}
            timeouts={homeTimeouts}
            isClient={isClient}
          />
          <div className={styles.logoSpacer} />
        </div>
        <AbsPositionedLogos homeTeam={homeTeam} awayTeam={awayTeam} />
      </div>
      {(!isTabletUp || !isClient) && (
        <ScoreTable
          scoreboard={scoreboard}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
        />
      )}
    </>
  );
};

export default LivePostMatchupMain;

interface AbsPositionedLogosProps {
  homeTeam: MatchupBasicTeam;
  awayTeam: MatchupBasicTeam;
}

const AbsPositionedLogos = ({
  homeTeam,
  awayTeam,
}: AbsPositionedLogosProps) => {
  return (
    <>
      <Image
        width={100}
        height={100}
        className={styles.homeTeamLogo}
        src={homeTeam.logoSVG}
        alt="Home Team Logo"
      />
      <div
        className={styles.homeTeamBackground}
        style={{ backgroundColor: homeTeam.primaryColor }}
      />
      <div
        className={styles.homeStripe}
        style={{ backgroundColor: homeTeam.secondaryColor }}
      />
      <Image
        width={100}
        height={100}
        className={styles.awayTeamLogo}
        src={awayTeam.logoSVG}
        alt="Away Team Logo"
      />
      <div
        className={styles.awayTeamBackground}
        style={{ backgroundColor: awayTeam.primaryColor }}
      />
      <div
        className={styles.awayStripe}
        style={{ backgroundColor: awayTeam.secondaryColor }}
      />
    </>
  );
};
interface TeamTextProps {
  teamName: string;
  sbkUrl?: string | null;
  opponentWin: boolean;
  isWinner: boolean;
  teamScore?: number | null;
  teamRecord?: BasicTeam["record"];
  isTabletUp?: boolean;
  isHome: boolean;
  hasPossession: boolean;
  timeouts?: number | null;
  isClient: boolean;
}
const TeamText = ({
  teamName,
  teamRecord,
  sbkUrl,
  opponentWin,
  isWinner,
  teamScore,
  isTabletUp,
  isHome,
  hasPossession,
  timeouts,
  isClient,
}: TeamTextProps) => {
  const { gameState, eventName } = useHeadToHeadContext();
  const isLive = gameState === EventStatus.Live;
  const isFinal = gameState === EventStatus.Final;
  const { logAmplitudeEvent } = useAmplitude();
  const handleClick = () => {
    logAmplitudeEvent(HEAD_TO_HEAD_TEAM_LINK_CLICKED, {
      "Link Text": teamName,
      "Link URL": sbkUrl ?? SPORTSBOOK_URL,
      "Event Name": eventName,
      Module: "header",
      "Team Name": teamName,
      "Page State": HEAD_TO_HEAD_STATUS_MAP[gameState],
    });
  };
  const timeOutCount = timeouts ?? 0;
  return (
    <div className={styles.teamText}>
      <a onClick={handleClick} href={sbkUrl ?? SPORTSBOOK_URL}>
        <h3>{teamName}</h3>
      </a>
      {(!isTabletUp || !isClient) && (
        <div className={cx(styles.score, { [styles.loss]: opponentWin })}>
          {teamScore ?? "-"}
          {isWinner && (
            <BettingTrophy
              className={isHome ? styles.homeTrophy : styles.awayTrophy}
            />
          )}
          {hasPossession && (
            <ColouredIconsFootball
              className={isHome ? styles.homeTrophy : styles.awayTrophy}
            />
          )}
        </div>
      )}
      {isFinal && (
        <div className={styles.record}>{formatNFLRecord(teamRecord)}</div>
      )}
      {isLive && (
        <div className={styles.timeouts}>
          <div
            className={cx(styles.timeout, timeOutCount < 1 && styles.open)}
          />
          <div
            className={cx(styles.timeout, timeOutCount < 2 && styles.open)}
          />
          <div
            className={cx(styles.timeout, timeOutCount < 3 && styles.open)}
          />
        </div>
      )}
    </div>
  );
};

interface ScoreTableProps {
  scoreboard: Event["scoreboard"];
  homeTeam: MatchupBasicTeam;
  awayTeam: MatchupBasicTeam;
}

const ScoreTable = ({ scoreboard, homeTeam, awayTeam }: ScoreTableProps) => {
  const { gameState } = useHeadToHeadContext();
  const isLive = gameState === EventStatus.Live;
  const isFinal = gameState === EventStatus.Final;
  const hasOverTime = scoreboard?.overtime?.home || scoreboard?.overtime?.away;
  return (
    <div className={styles.tableContainer}>
      <h3 className={cx(styles.tableHeading, isLive && styles.gameStatusLive)}>
        {isFinal ? "FINAL" : (scoreboard?.clock ?? "")}
      </h3>
      <table>
        <thead>
          <tr>
            <th />
            <th>Q1</th>
            <th>Q2</th>
            <th>Q3</th>
            <th>Q4</th>
            {hasOverTime && <th>OT</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{homeTeam.abbreviation ?? "Home"}</td>
            <td>{scoreboard?.firstQuarter?.home ?? "-"}</td>
            <td>{scoreboard?.secondQuarter?.home ?? "-"}</td>
            <td>{scoreboard?.thirdQuarter?.home ?? "-"}</td>
            <td>{scoreboard?.fourthQuarter?.home ?? "-"}</td>
            {hasOverTime && <td>{scoreboard?.overtime?.home ?? "-"}</td>}
          </tr>
          <tr>
            <td>{awayTeam.abbreviation ?? "Away"}</td>
            <td>{scoreboard?.firstQuarter?.away ?? "-"}</td>
            <td>{scoreboard?.secondQuarter?.away ?? "-"}</td>
            <td>{scoreboard?.thirdQuarter?.away ?? "-"}</td>
            <td>{scoreboard?.fourthQuarter?.away ?? "-"}</td>
            {hasOverTime && <td>{scoreboard?.overtime?.away ?? "-"}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
